import "./CompanyProducts.css";
import DataTable from "./components/DataTable.jsx";
import { usePermissions } from "./PermissionContext";
import ProductVariantsModal from "./components/ProductVariantsModal";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "/api";

const emptyProduct = {
  sku: "",
  name: "",
  description: "",
  category: "",
  tax_group_id: "",
  purchase_price: "",
  sale_price: "",
  minimum_stock: 0,
};

function getHeaders() {
  const token = localStorage.getItem("company_token");

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
}

function formatCurrency(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function getTaxTreatmentLabel(treatment) {
  const labels = {
    taxable: "Gravado",
    excluded: "Excluido de IVA",
    exempt: "Exento de IVA",
    not_applicable: "No aplica",
  };

  return labels[treatment] || "Sin definir";
}

function CompanyProducts() {
  const { can } = usePermissions();

  const canManageProducts = can("products.manage");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);

  const [categories, setCategories] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  const [taxRules, setTaxRules] = useState([]);

  const [formData, setFormData] = useState(emptyProduct);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const [
    selectedProductForVariants,
    setSelectedProductForVariants,
  ] = useState(null);

  const fileInputRef = useRef(null);

  const fetchProducts = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/products/me`,
      getHeaders()
    );

    setProducts(response.data);
  }, []);

  const fetchTaxCatalog = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/product-tax/me/catalog`,
      getHeaders()
    );

    setCategories(response.data.categories || []);
    setTaxGroups(response.data.tax_groups || []);
    setTaxRules(response.data.tax_rules || []);
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);

      await Promise.all([
        fetchProducts(),
        fetchTaxCatalog(),
      ]);
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible cargar la información de productos."
      );
    } finally {
      setLoading(false);
    }
  }, [fetchProducts, fetchTaxCatalog]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const getTaxInformation = useCallback(
    (taxGroupId) => {
      if (!taxGroupId) {
        return {
          group: null,
          rule: null,
        };
      }

      const group = taxGroups.find(
        (item) => Number(item.id) === Number(taxGroupId)
      );

      if (!group) {
        return {
          group: null,
          rule: null,
        };
      }

      const rule = taxRules.find(
        (item) =>
          Number(item.id) === Number(group.tax_rule_id)
      );

      return {
        group,
        rule: rule || null,
      };
    },
    [taxGroups, taxRules]
  );

  const createTaxInformation = useMemo(
    () => getTaxInformation(formData.tax_group_id),
    [formData.tax_group_id, getTaxInformation]
  );

  const editTaxInformation = useMemo(
    () => getTaxInformation(editingProduct?.tax_group_id),
    [editingProduct?.tax_group_id, getTaxInformation]
  );

  const handleCreateChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleCreateProduct = async (event) => {
    event.preventDefault();

    if (!canManageProducts) {
      toast.error(
        "No tienes permiso para administrar productos."
      );
      return;
    }

    if (!formData.tax_group_id) {
      toast.error("Debes seleccionar un grupo tributario.");
      return;
    }

    try {
      await axios.post(
        `${API_URL}/products/me`,
        {
          ...formData,
          category: formData.category || null,
          description: formData.description || null,
          tax_group_id: Number(formData.tax_group_id),
          purchase_price: Number(formData.purchase_price),
          sale_price: Number(formData.sale_price),
          minimum_stock: Number(formData.minimum_stock),
        },
        getHeaders()
      );

      toast.success("Producto creado correctamente.");

      setFormData(emptyProduct);

      await fetchProducts();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "No fue posible crear el producto."
      );
    }
  };

  const downloadProductsTemplate = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/products/template`,
        {
          ...getHeaders(),
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data])
      );

      const link = document.createElement("a");

      link.href = url;
      link.setAttribute(
        "download",
        "plantilla_productos.xlsx"
      );

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(
        "No fue posible descargar la plantilla Excel."
      );
    }
  };

  const importProductsExcel = async (event) => {
    if (!canManageProducts) {
      toast.error(
        "No tienes permiso para administrar productos."
      );
      return;
    }

    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error(
        "Selecciona un archivo Excel con extensión .xlsx."
      );

      event.target.value = "";
      return;
    }

    const excelData = new FormData();

    excelData.append("file", file);

    try {
      setIsImporting(true);

      const response = await axios.post(
        `${API_URL}/products/me/import-excel`,
        excelData,
        getHeaders()
      );

      toast.success(
        response.data.message ||
          `${response.data.created} productos importados correctamente.`
      );

      await fetchProducts();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "No fue posible importar el archivo Excel."
      );
    } finally {
      setIsImporting(false);
      event.target.value = "";
    }
  };

  const openEditModal = useCallback((product) => {
    setEditingProduct({
      id: product.id,
      sku: product.sku || "",
      name: product.name || "",
      description: product.description || "",
      category: product.category || "",
      tax_group_id: product.tax_group_id
        ? String(product.tax_group_id)
        : "",
      purchase_price: product.purchase_price ?? "",
      sale_price: product.sale_price ?? "",
      minimum_stock: product.minimum_stock ?? 0,
      is_active: product.is_active,
    });

    setShowEditModal(true);
  }, []);

  const openVariantsModal = () => {
    if (!editingProduct) {
      return;
    }

    setShowEditModal(false);

    setSelectedProductForVariants({
      id: editingProduct.id,
      name: editingProduct.name,
      sku: editingProduct.sku,
      sale_price: editingProduct.sale_price,
      is_active: editingProduct.is_active,
    });
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;

    setEditingProduct((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const saveProduct = async () => {
    if (!canManageProducts) {
      toast.error(
        "No tienes permiso para administrar productos."
      );
      return;
    }

    if (!editingProduct.tax_group_id) {
      toast.error("Debes seleccionar un grupo tributario.");
      return;
    }

    try {
      await axios.put(
        `${API_URL}/products/${editingProduct.id}`,
        {
          sku: editingProduct.sku,
          name: editingProduct.name,
          description: editingProduct.description || null,
          category: editingProduct.category || null,
          tax_group_id: Number(editingProduct.tax_group_id),
          purchase_price: Number(
            editingProduct.purchase_price
          ),
          sale_price: Number(editingProduct.sale_price),
          minimum_stock: Number(
            editingProduct.minimum_stock
          ),
        },
        getHeaders()
      );

      toast.success("Ficha del producto actualizada.");

      setShowEditModal(false);
      setEditingProduct(null);

      await fetchProducts();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "No fue posible actualizar el producto."
      );
    }
  };

  const toggleProductStatus = useCallback(
    async (product) => {
      if (!canManageProducts) {
        toast.error(
          "No tienes permiso para administrar productos."
        );
        return;
      }

      const newStatus = !product.is_active;
      const action = newStatus ? "activar" : "inactivar";

      if (
        !window.confirm(
          `¿Deseas ${action} el producto "${product.name}"?`
        )
      ) {
        return;
      }

      try {
        await axios.patch(
          `${API_URL}/products/${product.id}/status`,
          {
            is_active: newStatus,
          },
          getHeaders()
        );

        toast.success(
          `Producto ${
            newStatus ? "activado" : "inactivado"
          } correctamente.`
        );

        await fetchProducts();
      } catch (error) {
        toast.error(
          error.response?.data?.detail ||
            `No fue posible ${action} el producto.`
        );
      }
    },
    [fetchProducts, canManageProducts]
  );

  const columns = useMemo(
    () => [
      {
        title: "Código",
        data: "sku",
      },
      {
        title: "Producto",
        data: "name",
      },
      {
        title: "Categoría",
        data: "category",
        render: (category) =>
          category || "Sin categoría",
      },
      {
        title: "Impuesto",
        data: "tax_rule_name",
        render: (_, __, row) => {
          if (!row.tax_rule_name) {
            return (
              '<span class="tax-badge tax-pending">' +
              "Sin configurar" +
              "</span>"
            );
          }

          const taxClass = {
            taxable: "tax-taxable",
            excluded: "tax-excluded",
            exempt: "tax-exempt",
            not_applicable: "tax-not-applicable",
          }[row.tax_treatment];

          const rate =
            Number(row.tax_rate || 0) > 0
              ? ` · ${Number(row.tax_rate)}%`
              : "";

          return (
            `<span class="tax-badge ${taxClass || ""}">` +
            `${row.tax_rule_name}${rate}` +
            "</span>"
          );
        },
      },
      {
        title: "Compra",
        data: "purchase_price",
        render: (price) => formatCurrency(price),
      },
      {
        title: "Venta",
        data: "sale_price",
        render: (price) => formatCurrency(price),
      },
      {
        title: "Stock mínimo",
        data: "minimum_stock",
      },
      {
        title: "Estado",
        data: "is_active",
        render: (isActive) =>
          isActive
            ? '<span class="status active">Activo</span>'
            : '<span class="status inactive">Inactivo</span>',
      },
    ],
    []
  );

  const renderTaxPreview = (taxInformation) => {
    const { group, rule } = taxInformation;

    if (!group || !rule) {
      return (
        <div className="tax-preview tax-preview-empty">
          Selecciona un grupo tributario para conocer el
          impuesto.
        </div>
      );
    }

    const rate = Number(rule.rate || 0);

    return (
      <div className="tax-preview">
        <div>
          <span>Tratamiento tributario</span>
          <strong>
            {getTaxTreatmentLabel(rule.treatment)}
          </strong>
        </div>

        <div>
          <span>Tarifa</span>
          <strong>{rate}%</strong>
        </div>

        <p>
          {rule.description ||
            group.description ||
            "Regla tributaria configurada para el grupo."}
        </p>
      </div>
    );
  };

  if (loading) {
    return <h2>Cargando productos...</h2>;
  }

  return (
    <div className="company-products">
      <div className="products-heading">
        <div>
          <h1>Productos</h1>

          <p>
            Gestiona las fichas, precios, impuestos y estado
            de tus productos.
          </p>

          {canManageProducts && (
            <div className="product-import-actions">
              <button
                type="button"
                className="btn-download-template"
                onClick={downloadProductsTemplate}
              >
                Descargar plantilla
              </button>

              <button
                type="button"
                className="btn-import-excel"
                onClick={() => fileInputRef.current?.click()}
                disabled={isImporting}
              >
                {isImporting
                  ? "Importando..."
                  : "Importar Excel"}
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                hidden
                onChange={importProductsExcel}
              />
            </div>
          )}
        </div>

        <div className="products-summary">
          <strong>{products.length}</strong>
          <span>Productos registrados</span>
        </div>
      </div>

      {canManageProducts && (
        <form
          className="product-form"
          onSubmit={handleCreateProduct}
        >
          <h2>Crear producto</h2>

          <div className="product-form-grid">
            <div className="floating-field">
              <input
                type="text"
                id="create-sku"
                name="sku"
                placeholder=" "
                value={formData.sku}
                onChange={handleCreateChange}
                required
              />
              <label htmlFor="create-sku">Código o SKU</label>
            </div>

            <div className="floating-field">
              <input
                type="text"
                id="create-name"
                name="name"
                placeholder=" "
                value={formData.name}
                onChange={handleCreateChange}
                required
              />
              <label htmlFor="create-name">
                Nombre del producto
              </label>
            </div>

            <div className="floating-field">
              <select
                id="create-category"
                name="category"
                value={formData.category}
                onChange={handleCreateChange}
              >
                <option value="">
                  Selecciona una categoría
                </option>

                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              <label htmlFor="create-category">Categoría</label>
            </div>

            <div className="floating-field">
              <select
                id="create-tax-group"
                name="tax_group_id"
                value={formData.tax_group_id}
                onChange={handleCreateChange}
                required
              >
                <option value="">
                  Selecciona un grupo tributario
                </option>

                {taxGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
              <label htmlFor="create-tax-group">
                Grupo tributario
              </label>
            </div>

            <div className="floating-field">
              <input
                type="number"
                id="create-minimum-stock"
                name="minimum_stock"
                placeholder=" "
                min="0"
                value={formData.minimum_stock}
                onChange={handleCreateChange}
                required
              />
              <label htmlFor="create-minimum-stock">
                Stock mínimo
              </label>
            </div>

            <div className="floating-field">
              <input
                type="number"
                id="create-purchase-price"
                name="purchase_price"
                placeholder=" "
                min="0"
                step="0.01"
                value={formData.purchase_price}
                onChange={handleCreateChange}
                required
              />
              <label htmlFor="create-purchase-price">
                Precio de compra
              </label>
            </div>

            <div className="floating-field">
              <input
                type="number"
                id="create-sale-price"
                name="sale_price"
                placeholder=" "
                min="0.01"
                step="0.01"
                value={formData.sale_price}
                onChange={handleCreateChange}
                required
              />
              <label htmlFor="create-sale-price">
                Precio de venta
              </label>
            </div>
          </div>

          {renderTaxPreview(createTaxInformation)}

          <div className="floating-field floating-textarea">
            <textarea
              id="create-description"
              name="description"
              placeholder=" "
              value={formData.description}
              onChange={handleCreateChange}
              rows="3"
            />
            <label htmlFor="create-description">
              Descripción del producto
            </label>
          </div>

          <button type="submit">Crear producto</button>
        </form>
      )}

      <div className="products-table">
        <DataTable
          data={products}
          columns={columns}
          onEdit={
            canManageProducts ? openEditModal : undefined
          }
          onToggleActive={
            canManageProducts
              ? toggleProductStatus
              : undefined
          }
        />
      </div>

      {showEditModal && editingProduct && (
        <div className="modal-overlay">
          <div className="product-edit-modal">
            <div className="modal-header">
              <h2>Editar ficha de producto</h2>
            </div>

            <div className="modal-body">
              <div className="product-form-grid">
                <div className="floating-field">
                  <input
                    type="text"
                    id="edit-sku"
                    name="sku"
                    placeholder=" "
                    value={editingProduct.sku}
                    onChange={handleEditChange}
                    required
                  />
                  <label htmlFor="edit-sku">
                    Código o SKU
                  </label>
                </div>

                <div className="floating-field">
                  <input
                    type="text"
                    id="edit-name"
                    name="name"
                    placeholder=" "
                    value={editingProduct.name}
                    onChange={handleEditChange}
                    required
                  />
                  <label htmlFor="edit-name">
                    Nombre del producto
                  </label>
                </div>

                <div className="floating-field">
                  <select
                    id="edit-category"
                    name="category"
                    value={editingProduct.category}
                    onChange={handleEditChange}
                  >
                    <option value="">
                      Selecciona una categoría
                    </option>

                    {editingProduct.category &&
                      !categories.includes(
                        editingProduct.category
                      ) && (
                        <option
                          value={editingProduct.category}
                        >
                          {editingProduct.category}
                        </option>
                      )}

                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>

                  <label htmlFor="edit-category">
                    Categoría
                  </label>
                </div>

                <div className="floating-field">
                  <select
                    id="edit-tax-group"
                    name="tax_group_id"
                    value={editingProduct.tax_group_id}
                    onChange={handleEditChange}
                    required
                  >
                    <option value="">
                      Selecciona un grupo tributario
                    </option>

                    {taxGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>

                  <label htmlFor="edit-tax-group">
                    Grupo tributario
                  </label>
                </div>

                <div className="floating-field">
                  <input
                    type="number"
                    id="edit-minimum-stock"
                    name="minimum_stock"
                    placeholder=" "
                    min="0"
                    value={editingProduct.minimum_stock}
                    onChange={handleEditChange}
                    required
                  />
                  <label htmlFor="edit-minimum-stock">
                    Stock mínimo
                  </label>
                </div>

                <div className="floating-field">
                  <input
                    type="number"
                    id="edit-purchase-price"
                    name="purchase_price"
                    placeholder=" "
                    min="0"
                    step="0.01"
                    value={editingProduct.purchase_price}
                    onChange={handleEditChange}
                    required
                  />
                  <label htmlFor="edit-purchase-price">
                    Precio de compra
                  </label>
                </div>

                <div className="floating-field">
                  <input
                    type="number"
                    id="edit-sale-price"
                    name="sale_price"
                    placeholder=" "
                    min="0.01"
                    step="0.01"
                    value={editingProduct.sale_price}
                    onChange={handleEditChange}
                    required
                  />
                  <label htmlFor="edit-sale-price">
                    Precio de venta
                  </label>
                </div>
              </div>

              {renderTaxPreview(editTaxInformation)}

              <div className="floating-field floating-textarea">
                <textarea
                  id="edit-description"
                  name="description"
                  placeholder=" "
                  value={editingProduct.description}
                  onChange={handleEditChange}
                  rows="3"
                />
                <label htmlFor="edit-description">
                  Descripción del producto
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="product-variants-button"
                onClick={openVariantsModal}
              >
                Gestionar variantes
              </button>

              <button
                type="button"
                className="btn-save"
                onClick={saveProduct}
              >
                Guardar cambios
              </button>

              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingProduct(null);
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedProductForVariants && (
        <ProductVariantsModal
          product={selectedProductForVariants}
          getHeaders={() => getHeaders().headers}
          onClose={() =>
            setSelectedProductForVariants(null)
          }
        />
      )}
    </div>
  );
}

export default CompanyProducts;